import { apiAuthorizationHeaders } from '@/config/apiAuth';
import type {
  EEvidenceSubmissionDocument,
  EEvidenceSubmissionRequest,
  EEvidenceSubmissionResult,
  LensApiResponse,
} from './contracts';
import { eEvidenceFailure } from './errors';

const SUBMISSIONS_ENDPOINT = '/api/v1/cases/e-evidence';

function isSubmissionDocument(value: unknown): value is EEvidenceSubmissionDocument {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const document = value as Partial<EEvidenceSubmissionDocument>;
  return (
    Number.isInteger(document.index) &&
    typeof document.documentType === 'string' &&
    typeof document.fileName === 'string' &&
    typeof document.attachState === 'string'
  );
}

function isSubmissionResult(value: unknown): value is EEvidenceSubmissionResult {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const result = value as Partial<EEvidenceSubmissionResult>;
  return (
    typeof result.submissionState === 'string' &&
    typeof result.caseId === 'string' &&
    Array.isArray(result.documents) &&
    result.documents.every(isSubmissionDocument)
  );
}

async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw eEvidenceFailure('InvalidResponse', response.status);
  }
}

// 201 and 200 both carry the envelope; a refusal is a 200 with isSuccess false.
function unwrapResult(body: unknown, status: number): EEvidenceSubmissionResult {
  if (typeof body === 'object' && body !== null && 'isSuccess' in body) {
    const envelope = body as LensApiResponse<EEvidenceSubmissionResult>;
    if (envelope.isSuccess && isSubmissionResult(envelope.data)) {
      return envelope.data;
    }

    throw eEvidenceFailure(envelope.error?.code ?? 'InvalidResponse', status);
  }

  throw eEvidenceFailure('InvalidResponse', status);
}

async function readFailure(response: Response): Promise<never> {
  let code: string | undefined;

  try {
    const body = (await response.json()) as
      | LensApiResponse<unknown>
      | { code?: string; extensions?: { code?: string } };

    if ('error' in body && body.error !== undefined) {
      code = body.error.code;
    } else if ('code' in body && typeof body.code === 'string') {
      code = body.code;
    } else if ('extensions' in body && typeof body.extensions?.code === 'string') {
      code = body.extensions.code;
    }
  } catch {
    // The portal owns the fallback message, so an unreadable server body is ignored.
  }

  if (code === undefined && response.status === 401) {
    code = 'Unauthenticated';
  }

  throw eEvidenceFailure(code, response.status);
}

function unauthenticated() {
  return eEvidenceFailure('Unauthenticated', 0);
}

export async function submitEEvidenceOrder(
  request: EEvidenceSubmissionRequest,
  idempotencyKey: string,
): Promise<EEvidenceSubmissionResult> {
  const form = new FormData();
  form.append('requestType', request.requestType);
  if (request.requestType === 'epoc' && request.isEmergency !== undefined) {
    form.append('isEmergency', String(request.isEmergency));
  }
  form.append('requestPdf', request.requestPdf, request.requestPdf.name);
  for (const document of request.supportingDocuments ?? []) {
    form.append('supportingDocuments', document, document.name);
  }

  const authorization = await apiAuthorizationHeaders(unauthenticated);

  let response: Response;
  try {
    response = await fetch(SUBMISSIONS_ENDPOINT, {
      method: 'POST',
      headers: { ...authorization, 'X-Idempotency-Key': idempotencyKey },
      body: form,
    });
  } catch {
    throw eEvidenceFailure('TransportFailure', 0);
  }

  if (!response.ok) {
    return readFailure(response);
  }

  return unwrapResult(await readBody(response), response.status);
}